import React from 'react';
import Icon from '../../../components/AppIcon';

const EngagementHeatmap = ({ data }) => {
  const getEngagementLevel = (score) => {
    if (score >= 80) return { label: 'Excellent', color: 'bg-success', textColor: 'text-success' };
    if (score >= 60) return { label: 'Good', color: 'bg-primary', textColor: 'text-primary' };
    if (score >= 40) return { label: 'Fair', color: 'bg-warning', textColor: 'text-warning' };
    return { label: 'Low', color: 'bg-destructive', textColor: 'text-destructive' };
  };

  const calculateWeeklyPattern = () => {
    if (!data || data?.length === 0) return [];

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayData = weekDays?.map(() => ({ total: 0, count: 0 }));

    data?.forEach(item => {
      const date = new Date(item?.date);
      const dayIndex = date?.getDay();
      dayData[dayIndex].total += item?.engagement_score || 0;
      dayData[dayIndex].count += 1;
    });

    return weekDays?.map((day, index) => ({
      day,
      avgScore: dayData?.[index]?.count > 0
        ? Math?.round(dayData?.[index]?.total / dayData?.[index]?.count)
        : 0
    }));
  };

  const weeklyPattern = calculateWeeklyPattern();
  const avgEngagement = data?.length > 0
    ? Math?.round(data?.reduce((sum, item) => sum + (item?.engagement_score || 0), 0) / data?.length)
    : 0;

  const engagementLevel = getEngagementLevel(avgEngagement);

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Engagement Patterns</h2>
        <Icon name="Calendar" size={20} className="text-muted-foreground" />
      </div>

      {/* Overall Score */}
      <div className="bg-background rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground mb-1">Overall Engagement</div>
            <div className="text-3xl font-bold text-foreground">{avgEngagement}</div>
          </div>
          <div className={`px-3 py-1 rounded-full ${engagementLevel?.color} bg-opacity-20`}>
            <span className={`text-sm font-semibold ${engagementLevel?.textColor}`}>
              {engagementLevel?.label}
            </span>
          </div>
        </div>
      </div>

      {/* Weekly Heatmap */}
      <div className="space-y-2">
        <div className="text-sm font-semibold text-foreground mb-3">Weekly Activity Pattern</div>
        {weeklyPattern?.length === 0 ? (
          <div className="text-center py-6">
            <Icon name="Calendar" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No activity data available</p>
          </div>
        ) : (
          weeklyPattern?.map((dayData) => {
            const level = getEngagementLevel(dayData?.avgScore);
            return (
              <div key={dayData?.day} className="flex items-center gap-3">
                <div className="w-12 text-sm font-medium text-muted-foreground">
                  {dayData?.day}
                </div>
                <div className="flex-1 bg-background rounded-lg overflow-hidden">
                  <div
                    className={`h-8 ${level?.color} transition-all duration-300 flex items-center justify-end px-3`}
                    style={{ width: `${Math?.max(dayData?.avgScore, 5)}%` }}
                  >
                    <span className="text-xs font-semibold text-white">
                      {dayData?.avgScore}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="text-xs text-muted-foreground mb-2">Engagement Levels</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-success" />
            <span className="text-xs text-foreground">Excellent (80+)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-primary" />
            <span className="text-xs text-foreground">Good (60-79)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-warning" />
            <span className="text-xs text-foreground">Fair (40-59)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-destructive" />
            <span className="text-xs text-foreground">Low (0-39)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementHeatmap;